import { observer } from "mobx-react";
import React, { useMemo, useRef } from "react";
import { useInRouterContext } from "react-router";

import { Overflow, OverflowItem, useArrowNavigationGroup } from "@fluentui/react-components";

import { useAppLeftBarStyles } from "./AppLeftBar.styles";
import { AppLeftBarItem } from "./AppLeftBarItem/AppLeftBarItem";
import { useSideBarItems } from "./useSideBarItems";
import { LeftBarMatchItem, useCheckMatchedItem } from "./useCheckMatchedItem";
import { isUMSUrl } from "Shared/Utils/isUMSUrl";
import { umsSideBarItems } from "./AppLeftBarMenuItems";
import { OverflowSeparator } from "./OverflowSeparator";
import { usePinnedItems } from "./usePinnedItems";
import { OverflowMenu } from "./Overflow/OverflowMenu";
import { useSmallScreen } from "Shared/Hooks";

export interface AppLeftBarProps {
    onDismiss?: () => void;
}

const LeftBarBase = (props: AppLeftBarProps & { matchedItem: LeftBarMatchItem }) => {
    const { onDismiss, matchedItem } = props;
    const isSmallScreen = useSmallScreen();
    const classNames = useAppLeftBarStyles(isSmallScreen);
    const attributes = useArrowNavigationGroup({ axis: "vertical", circular: true });
    const pinnedAppsPulse = usePinnedItems();

    const { sideBarItems, addAppItem } = useSideBarItems();

    const pinnedApps = useMemo(() => (isUMSUrl ? [] : [...pinnedAppsPulse, addAppItem]), [pinnedAppsPulse, addAppItem]);

    const sideBarItemsToDisplay = isUMSUrl ? umsSideBarItems : sideBarItems;
    const overflowMenuApps = useMemo(() => [...sideBarItemsToDisplay, ...pinnedApps], [sideBarItemsToDisplay, pinnedApps]);

    return (
        <div className={classNames.root} role="none" {...attributes}>
            <Overflow overflowAxis="vertical" padding={30}>
                <div className={classNames.overflowContainer}>
                    {sideBarItemsToDisplay.map((item) => {
                        const isSelected = matchedItem.itemKey === item.displayName;
                        return (
                            <OverflowItem key={item.displayName} id={item.displayName} priority={isSelected ? 1000 : undefined}>
                                <div>
                                    <AppLeftBarItem item={item} matchedItem={matchedItem} onDismiss={onDismiss} />
                                </div>
                            </OverflowItem>
                        );
                    })}

                    <OverflowSeparator groupId="pinnedApps" />
                    {pinnedApps.map((app) => {
                        const isSelected = matchedItem.itemKey === app.displayName;
                        return (
                            <OverflowItem key={app.displayName} id={app.displayName} groupId="pinnedApps" priority={isSelected ? 1000 : undefined}>
                                <div>
                                    <AppLeftBarItem item={app} matchedItem={matchedItem} onDismiss={onDismiss} />
                                </div>
                            </OverflowItem>
                        );
                    })}

                    <OverflowMenu apps={overflowMenuApps} matchedItem={matchedItem} onDismiss={onDismiss} />
                </div>
            </Overflow>
        </div>
    );
};

const LeftBar = observer(LeftBarBase);

const LeftBarOutsideRouterBase = (props: AppLeftBarProps) => {
    const matchedItem = useRef({ itemKey: "" }).current;
    return <LeftBar {...props} matchedItem={matchedItem} />;
};

const LeftBarOutsideRouter = observer(LeftBarOutsideRouterBase);

const LeftBarInRouterBase = (props: AppLeftBarProps) => {
    const matchedItem = useCheckMatchedItem();
    return <LeftBar {...props} matchedItem={matchedItem} />;
};

const LeftBarInRouter = observer(LeftBarInRouterBase);

const AppLeftBarBase = (props: AppLeftBarProps) => {
    const inRouter = useInRouterContext();
    return inRouter ? <LeftBarInRouter {...props} /> : <LeftBarOutsideRouter {...props} />;
};

export const AppLeftBar = observer(AppLeftBarBase);
